/**
 * @file src/_modulos/prenota/tabela/layouts/tabela/columns/components/data.tsx
 * @description Componentes e funções relacionadas a datas e vencimento das pré-notas
 */

"use client";

import { fmt, parse } from "visuais";
import {
  IconClockFilled,
  IconCircleCheckFilled,
  IconAlertCircleFilled,
} from "@tabler/icons-react";
import {
  BadgePreset,
  CategoryConfig,
  createBadgePreset,
  createEmptyPreset,
  createInvalidPreset,
} from "./utils";

/**
 * Configuração das categorias de vencimento
 */
const VENCIMENTO_CATEGORIES: Record<string, CategoryConfig> = {
  vencido: {
    color: "text-rose-600 dark:text-rose-400",
    icon: IconAlertCircleFilled,
    getTooltip: () => "", // Será sobrescrito dinamicamente
  },
  proximos: {
    color: "text-lime-600 dark:text-lime-400",
    icon: IconClockFilled,
    getTooltip: () => "", // Será sobrescrito dinamicamente
  },
  futuro: {
    color: "text-indigo-600 dark:text-indigo-400",
    icon: IconCircleCheckFilled,
    getTooltip: () => "", // Será sobrescrito dinamicamente
  },
} as const;

/**
 * Obtém preset de vencimento
 * Suporta múltiplas datas separadas por vírgula
 * Verifica se vencimentos estão pagos (tem baixa e saldo = 0)
 */
export function getVencimentoPreset(
  vencStr?: string | null,
  baixaStr?: string | null,
  saldoStr?: string | null
): BadgePreset {
  const raw = vencStr?.trim() ?? "";

  if (!raw) {
    return createEmptyPreset(
      <span>Sem vencimento</span>,
      "Documento sem classificação e título financeiro."
    );
  }

  // Separar múltiplas datas por vírgula
  const datesStr = raw.split(",").map((d) => d.trim()).filter(Boolean);
  
  // Separar baixas e saldos
  const baixas = baixaStr?.split(",").map((b) => b.trim()) || [];
  const saldos = saldoStr?.split(",").map((s) => {
    const trimmed = s.trim();
    return trimmed ? parseFloat(trimmed) : null;
  }) || [];
  
  // Processar todas as datas válidas e associar baixas/saldos ANTES de ordenar
  const dates: Array<{ date: Date; diff: number; label: string; isPago: boolean; baixaLabel?: string }> = [];
  
  for (let i = 0; i < datesStr.length; i++) {
    const dateStr = datesStr[i];
    const date =
      parse.dates.YYYYMMDD(dateStr, { allowNull: true }) ||
      parse.dates.BR(dateStr, { allowNull: true });

    if (date instanceof Date) {
      const diff = fmt.dates.differenceInCalendarDays(date);
      const label = fmt.dates.BR(date);
      
      // Associar baixa e saldo por índice ANTES de ordenar
      const temBaixa = Boolean(i < baixas.length && baixas[i] && baixas[i].trim() !== "");
      const saldo = i < saldos.length ? saldos[i] : null;
      const isPago = temBaixa && saldo !== null && saldo === 0;
      
      let baixaLabel: string | undefined;
      if (temBaixa && i < baixas.length) {
        const baixaDate = parse.dates.YYYYMMDD(baixas[i].trim(), { allowNull: true });
        if (baixaDate instanceof Date) {
          baixaLabel = fmt.dates.BR(baixaDate);
        }
      }
      
      dates.push({ date, diff, label, isPago, baixaLabel });
    }
  }
  
  // Ordenar por data (mais antiga primeiro) DEPOIS de associar tudo
  dates.sort((a, b) => a.date.getTime() - b.date.getTime());

  // Se nenhuma data válida foi encontrada
  if (dates.length === 0) {
    return createInvalidPreset(
      raw,
      ["YYYYMMdd", "dd/MM/yyyy"],
      "Data Inválida"
    );
  }

  // Verificar se todas estão pagas
  const todasPagas = dates.every((d) => d.isPago);
  
  // Determinar categoria baseada na primeira data não paga
  const closestDateNaoPago = dates.find((d) => !d.isPago);
  let categoryKey: string;
  
  if (todasPagas) {
    // Se todas estão pagas, usar categoria futuro (verde/azul)
    categoryKey = "futuro";
  } else if (closestDateNaoPago) {
    // Se há pelo menos uma não paga, usar a categoria dela
    if (closestDateNaoPago.diff < 0) {
      categoryKey = "vencido";
    } else if (closestDateNaoPago.diff <= 5) {
      categoryKey = "proximos";
    } else {
      categoryKey = "futuro";
    }
  } else {
    // Fallback
    categoryKey = "futuro";
  }

  const config = VENCIMENTO_CATEGORIES[categoryKey];

  // Criar label: primeira data não paga (ou primeira se todas estiverem pagas) + indicação de múltiplas se houver
  const dataParaLabel = todasPagas ? dates[0] : (closestDateNaoPago || dates[0]);
  const firstLabel = dataParaLabel.label;
  const label = dates.length > 1 
    ? `${firstLabel} (+${dates.length - 1})`
    : firstLabel;

  // Criar tooltip com informações de dias e datas (ordenar por data: mais antiga primeiro)
  const tooltip = dates
    .sort((a, b) => a.date.getTime() - b.date.getTime()) // Ordenar por data: mais antiga primeiro
    .map((d) => {
      if (d.isPago && d.baixaLabel) {
        return `- Venc: ${d.label} - Pago: ${d.baixaLabel}`;
      } else if (d.diff < 0) {
        return `- Vencido há ${Math.abs(d.diff)} dia(s): ${d.label}`;
      } else if (d.diff === 0) {
        return `- Vence hoje: ${d.label}`;
      } else {
        return `- Vence em ${d.diff} dia(s): ${d.label}`;
      }
    }).join("\n");

  // Criar preset personalizado para data com tooltip dinâmico
  // Se todas pagas, usar ícone de check e cor neutra
  const configFinal = todasPagas ? VENCIMENTO_CATEGORIES.futuro : config;
  const color = todasPagas ? "" : config.color;
  const preset = createBadgePreset(configFinal, label, color);

  return {
    ...preset,
    tooltip,
  };
}
